import React from 'react';
import classNames from 'classnames';
import { PropTypes } from 'prop-types';
import { useTheme } from '@emotion/react';
import { useNavigate } from 'react-router-dom';
import { formatRoute } from 'react-router-named-routes';
import { Card, CardMedia, Grid, Box } from '@mui/material';
import { MY_DAY_CONTENT_ENTRY_ROUTE } from '../../services/routes';
import { handleClickContentCard } from '../../services/content_entries';
import {
  trackContentClick,
  useTracker,
} from '../../operations/tracking_operations';
import {
  useGetContentEntryByIDAndParentQuery,
  useGetSharedContentEntryByIDAndParentQuery,
  useGetUserProfileQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import MyDayPlaceholderCard from './my_day_placeholder_card';

const MyDayCompactContentCard = ({ contentEntryID, parentID }) => {
  const navigate = useNavigate();

  const tracker = useTracker();

  const theme = useTheme();

  const { data: userProfile } = useGetUserProfileQuery();

  const {
    data: ownContentEntry,
    isSuccess: isOwnContentEntrySuccess,
    isError: isOwnContentEntryError,
  } = useGetContentEntryByIDAndParentQuery(
    { id: contentEntryID, parentID },
    { skip: !contentEntryID || !parentID },
  );

  const {
    data: sharedContentEntry,
    isSuccess: isSharedContentEntrySuccess,
    isError: isSharedContentEntryError,
  } = useGetSharedContentEntryByIDAndParentQuery(
    { id: contentEntryID, parentID },
    { skip: !contentEntryID || !parentID },
  );

  const contentEntry = ownContentEntry || sharedContentEntry;

  const contentEntryExists = !!contentEntry;

  const title = contentEntry?.title;

  const coverImageUrl = contentEntry?.coverImageUrl;

  const appLink = contentEntry?.appLink;

  const hasTitle = !!title;

  const hasLoadedSuccessfully =
    isSharedContentEntrySuccess && isOwnContentEntrySuccess;
  const hasError = isSharedContentEntryError || isOwnContentEntryError;

  if (hasLoadedSuccessfully && !contentEntryExists) {
    return null;
  }

  if (hasError) {
    return null;
  }

  const handleClick = () =>
    handleClickContentCard(
      appLink,
      (hyperlink) => {
        trackContentClick(
          tracker,
          userProfile,
          contentEntryID,
          title,
          hyperlink,
          'My Day',
        );
      },
      navigate,
      () => {
        navigate(
          formatRoute(MY_DAY_CONTENT_ENTRY_ROUTE, {
            parentID,
            sharedContentEntryID: contentEntryID,
          }),
        );
      },
    );

  const imgClassNames = classNames('myDayCompactContentEntryCardImage', {
    withFilter: hasTitle,
  });

  return !hasLoadedSuccessfully ? (
    <MyDayPlaceholderCard />
  ) : (
    <Card
      className="my-day-compact-content-card"
      onClick={handleClick}
      sx={{ display: 'flex', flexDirection: 'column' }}
      style={{ backgroundColor: theme.palette.primary.cardBackground }}>
      <Box sx={{ position: 'relative' }}>
        {hasTitle && coverImageUrl && (
          <Grid item xs={12}>
            <CardMedia
              component="img"
              className={imgClassNames}
              image={coverImageUrl}
              alt=""
            />
            <div className="myDayCompactContentEntryCardText">{title}</div>
          </Grid>
        )}
        {!hasTitle && coverImageUrl && (
          <Grid item xs={12}>
            <CardMedia
              component="img"
              className={imgClassNames}
              image={coverImageUrl}
              alt=""
            />
          </Grid>
        )}
        {hasTitle && !coverImageUrl && (
          <div>
            <div className={imgClassNames} />
            <div className="myDayCompactContentEntryCardText">{title}</div>
          </div>
        )}
        {!hasTitle && !coverImageUrl && (
          <div>
            <div className={imgClassNames} />
          </div>
        )}
      </Box>
    </Card>
  );
};

MyDayCompactContentCard.propTypes = {
  contentEntryID: PropTypes.string.isRequired,
  parentID: PropTypes.string.isRequired,
};

export default MyDayCompactContentCard;
